import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Image as ImageIcon } from 'lucide-react';

import type { ProjectImageType } from '@/api/media';
import { getMediaOptions } from '@/api/media';
import type { UploadMediaModalType } from '@/features/estate-media/components/upload-media-modal';
import {
  ACCEPTED_IMAGE_INPUT_TYPE,
  uploadImagesSchema,
} from '@/features/estate-media/schema/upload-media';
import { useUploadImage } from '@/features/project-media/api/upload-image';
import { env } from '@/constants/env';
import {
  getLatestItem,
  MEDIA_SECTION_LINK_CLASS,
  MediaSectionCard,
} from '@/features/estate-media/components/media-section-card';
import { useModal } from '../../../../components/modals/new/use-modal';
import { UploadMediaModal } from '../../../estate-media/components/upload-media-modal';

type ImagesCardProps = {
  data: ProjectImageType[][];
  projectId: string;
  canWriteMedia: boolean;
};

export const ImagesCard = ({ data = [], projectId, canWriteMedia }: ImagesCardProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const uploadModal = useModal();

  const { mutateAsync: uploadImage } = useUploadImage({
    mutationConfig: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getMediaOptions(projectId).queryKey });
        toast.success(t('successfullyAddedImage'));
      },
    },
  });

  const images = data?.flat() || [];
  const amount = images.length;

  const submitHandler = async (data: UploadMediaModalType) => {
    const dataTransfer = new DataTransfer();
    data.files.forEach((file) => dataTransfer.items.add(file));
    await uploadImage({ images: dataTransfer.files, projectId });
    uploadModal.dismiss();
  };

  const handleOpenUploadImageModal = () => {
    uploadModal.open();
  };

  const latest = getLatestItem(images);

  return (
    <>
      <MediaSectionCard
        title={t('images')}
        icon={<ImageIcon className="h-4 w-4" />}
        count={amount}
        previewSrc={latest?.mediaId ? `${env.API_URL}/images/${latest.mediaId}` : undefined}
        link={
          <Link
            to="/projects/$projectId/media/images"
            params={{ projectId }}
            className={MEDIA_SECTION_LINK_CLASS}
            aria-label={t('images')}
          />
        }
        onUpload={canWriteMedia ? handleOpenUploadImageModal : undefined}
      />
      <UploadMediaModal
        modal={uploadModal}
        submitHandler={submitHandler}
        acceptedType={ACCEPTED_IMAGE_INPUT_TYPE}
        acceptedMediaSchema={uploadImagesSchema()}
      />
    </>
  );
};
